import { TableHeaderSyncMgr } from "@omnia/shared/components/tableHeaderSync"
import { getApplicationViewModels } from "../../../applications/applications"
import { ApplicationVm } from "../../../applications/applicationVm"
import { CroppingGroupVm } from "../../../croppingGroup"
import { defineDirective, getDistinctValues, ICrop, IOmniaShell, isArray, isArrayEmpty, shell } from "../../../omnia"
import { CroppingGroupService } from "../../../services/grouping"
import { NutrientFilter } from "../../../services/nutrientFilter"
import { setupStocks } from "../../../stocks/utils"
import { IFieldsService, INtrReq, INutrient, INutritionService, NutrientType } from "../../../types"
import { applyPlanFilter } from "../../../utils"
import { AppVm } from "../app"
import { MapContext } from "../map-context"
import { PlanVm } from "../plan"
import { PlanReader } from "../plan-reader"
import { DashboardSelection } from "../selection"
import { ICroppingApplication, ICroppingPlan, IDashboardContext, IDashboardFilters, IPlanCropping } from "../types"
import { CroppingVm } from "./cropping"
import { getCroppingIndexAttrs, getIndexData, getPlanIndexAttrs, IIndexDataAttr } from "./index-attr"
import { getCropCssColor } from "@omnia/shared/fields"
import { hasAuthorizationsOnAnyInGroup } from "./permissions"

export type CroppingGroup = CroppingGroupVm<CroppingVm>

export enum MenuView {
    CroppingIndex = 1,
    CroppingDetails = 2,
    PlansIndex = 3,
    PlanDetails = 4
}

export class MenuCtrl {

    static create(): ng.IDirective {

        let def = defineDirective(MenuCtrl, "app/nutrition/dashboard/map/menu/menu.html", {
            context: "=",
            filters: "=",
        })

        def.link = (scope, element, attrs, ctrl: MenuCtrl) => {
            ctrl.link(element)
        }

        return def

    }

    private readOnly = this.svc.readOnly

    private rootElement: HTMLElement
    private _headerSyncMgr: TableHeaderSyncMgr

    private _selectionWatcher: Function

    private _plan: PlanVm
    private _planApps: ApplicationVm[]

    private _visibleNutrients: INutrient[]

    public view: MenuView
    public lastIndexView: MenuView

    public allCropping: CroppingVm[]
    public visibleCropping: CroppingVm[]
    public visibleCroppingGroups: CroppingGroup[]

    public allPlans: PlanVm[]
    public visiblePlans: PlanVm[]

    public cropping: CroppingVm
    public app: ICroppingApplication
    public appVm: AppVm

    public mruList: (PlanVm | CroppingVm)[] = []

    public planIndexAttrs: IIndexDataAttr[]
    public planIndexAttr: IIndexDataAttr

    public croppingIndexAttrs: IIndexDataAttr[]
    public croppingIndexAttr: IIndexDataAttr


    public context: IDashboardContext
    protected _filters: IDashboardFilters

    protected _nutrients: NutrientFilter
    protected mapCtxt: MapContext


    static $inject = [
        "$scope",
        "OmniaShell",
        "OmniaNutritionService",
        "OmniaFieldsService",
        "OmniaNutritionCroppingGroup"
    ]

    constructor(
        private $scope: ng.IScope,
        private readonly shell: IOmniaShell,
        private readonly svc: INutritionService,
        private fieldsSvc: IFieldsService,
        private groupingSvc: CroppingGroupService,
        private readonly session = svc.getSession()) {

        $scope.$watch("vm.context", () => this.setupComponent())

    }

    $onDestroy() {

        if (this._selectionWatcher) {
            this._selectionWatcher()
            this._selectionWatcher = null
        }

        this.disposeHeaderSyncMgr()

    }

    private disposeHeaderSyncMgr() {

        if (this._headerSyncMgr) {
            this._headerSyncMgr.dispose()
            this._headerSyncMgr = null
        }

    }

    private link(element: any) {
        this.rootElement = element[0]
    }

    onIncludesLoaded() {

        this.disposeHeaderSyncMgr()

        this._headerSyncMgr = new TableHeaderSyncMgr()
        this._headerSyncMgr.setup(this.rootElement, ".scrollable > .content > .card-body .body > table", ".scrollable > .content > .card-body table.header")

    }


    get croppingView(): boolean {
        let s = this.selection
        return s && (!s.planMode)
    }

    get croppingIndexView(): boolean {
        return this.view == MenuView.CroppingIndex
    }

    get croppingDetailsView(): boolean {
        return this.view == MenuView.CroppingDetails
    }

    get planIndexView(): boolean {
        return this.view == MenuView.PlansIndex
    }

    get indexView(): boolean {
        return this.croppingIndexView || this.planIndexView
    }

    get planDetailsView(): boolean {
        return this.view == MenuView.PlanDetails
    }

    get selection(): DashboardSelection {
        if (this.context) {
            return this.context.selection
        }
    }

    set selection(value: DashboardSelection) {
        this.context.setSelection(value, "MenuCtrl")
    }

    get filters(): IDashboardFilters {
        return this._filters
    }

    set filters(value: IDashboardFilters) {
        this._filters = value
        this.applyFilters()
    }

    get planFilter() {
        return this.svc.planFilter
    }

    set planFilter(value: string) {
        this.svc.planFilter = value
        this.applyFilters()
    }

    get nutrient(): INutrient {
        if (this._nutrients) {
            return this._nutrients.nutrient
        }
    }

    get nutrients(): INutrient[] {
        if (this._nutrients) {
            return this._nutrients.nutrients
        }
    }

    get visibleNutrients(): INutrient[] {
        if (isArray(this._visibleNutrients)) {
            return this._visibleNutrients
        }
        return this.nutrients
    }

    get plan(): PlanVm {
        return this._plan
    }

    set plan(value: PlanVm) {

        this._plan = value
        this._visibleNutrients = null
        this._planApps = undefined

        if (value) {

            this._visibleNutrients = this._nutrients.getSubset(value.nutrients)

            const { svc } = this
            const stocks = setupStocks(svc)
            this._planApps = getApplicationViewModels(value.applications, svc, stocks)

        }

    }

    get planApplications() {
        return this._planApps
    }

    get selectedItem() {
        return this.cropping || this.plan
    }

    get groupings() {
        return this.groupingSvc.groupings
    }

    get currentGrouping() {
        return this.groupingSvc.currentGrouping
    }

    set currentGrouping(value) {
        this.groupingSvc.currentGrouping = value
        this.updateGroups()
    }

    get showMenu(): boolean {
        if (!this.context) return true
        return this.context.showMenu
    }

    get showFilters(): boolean {
        if (!this.context) return true
        return this.context.showFilters
    }

    protected setupComponent() {

        let ctxt = this.context

        if (!ctxt) {
            return
        }

        if (this._selectionWatcher) {
            this._selectionWatcher()
            this._selectionWatcher = null
        }

        this.mapCtxt = ctxt.mapCtxt
        this._nutrients = ctxt.nutrients

        this.setup()

        this._selectionWatcher = this.$scope.$watch("vm.context.selection", () => this.applySelection())

    }

    protected setup() {

        let reader = this.context.planReader

        this.setupCropping(reader)

        this.setupPlans(reader)

        this.setupIndexDataAttrs()

        this.setupMruList()

        this.applySelection()

        this.applyFilters()

    }

    private setupMruList() {

        if (this.mruList) {

            this.mruList = this.mruList.map(i => {

                let planId = i["planId"]
                if (planId) {
                    return this.allPlans.find(p => p.planId == planId)
                }

                let croppingId = i["croppingId"]
                if (croppingId) {
                    return this.allCropping.find(c => c.croppingId == croppingId)
                }

            }).filter(i => !!i)

        }
    }

    private setupIndexDataAttrs() {

        this.planIndexAttrs = getPlanIndexAttrs()
        this.planIndexAttr = this.planIndexAttrs[1]

        this.croppingIndexAttrs = getCroppingIndexAttrs()
        this.croppingIndexAttr = this.croppingIndexAttrs[1]

    }

    private setupPlans(reader: PlanReader) {

        this.allPlans = reader.getPlans()

    }

    private setupCropping(reader: PlanReader) {

        let cropping = this.fieldsSvc.cropping.filter(c => c.currentYear)

        this.allCropping = cropping.map(c => {
            let nmax = this.svc.getNMaxCroppingGroup(c.croppingId)
            return CroppingVm.create(c, nmax, reader)
        })

    }

    public showIndex(cropping: boolean) {

        let view = (cropping ? MenuView.CroppingIndex : MenuView.PlansIndex)
        let attr = (cropping ? this.croppingIndexAttr : this.planIndexAttr)
        this.view = this.lastIndexView = view
        this.selection = this.selection.clear(view == MenuView.PlansIndex, attr ? attr.mapView : null)
        this.mapCtxt.viewDefaultExtent()
        this.mapCtxt.clearSelection()

    }

    public showLastIndex() {
        this.showIndex(this.lastIndexView != MenuView.PlansIndex)
    }

    public selectCropping(croppingId: number) {

        let cropping = this.allCropping.find(c => c.croppingId == croppingId)
        if (!cropping) return

        this.cropping = cropping
        this.plan = null
        this.setCurrentApp(null)
        this.updateMruList(cropping)

        this.view = MenuView.CroppingDetails

        this.selection = this.selection.openCropping(croppingId)

        this.selectMapCropping(croppingId, this.nutrient)

    }

    public selectOpeningBalance(cropping: CroppingVm, nutrient: INutrient = null) {
        return this.selectCroppingRequirements(cropping, nutrient, -1)
    }

    public selectCroppingRequirements(cropping: CroppingVm, nutrient: INutrient = null, closing: number = 0) {

        if (!cropping) return

        this.cropping = cropping
        this.setCurrentApp(null)

        this.selection = this.selection.openCropping(cropping.croppingId, closing)

        nutrient = nutrient || this.nutrient

        if (nutrient) {
            this._nutrients.setNutrient(nutrient, false)
            this.selectMapCropping(cropping.croppingId, nutrient)
        }

    }

    private selectMapCropping(croppingId: number, nutrient: INutrient) {
        this.mapCtxt.selectViewFromSelection(this.nutrient, this.selection)
        this.mapCtxt.selectCropping(croppingId)
    }


    public selectApplication(vm: AppVm, nutrient?: INutrient) {

        let app = vm ? vm.app : null

        if (app && (!nutrient)) {

            if (this.nutrient && vm && vm.contributesNutrient(this.nutrient.key)) {
                nutrient = this.nutrient
            }
            else {
                nutrient = this.nutrients.find(n => n.nutrient == app.targetNutrient)
            }

            if (!nutrient) {
                nutrient = this.context.nutrients.getNutrient(app.targetNutrient)
            }

        }

        if (app && (app == this.app) && ((!nutrient) || (nutrient == this.nutrient))) {
            return
        }

        this.setCurrentApp(vm)

        if (app) {

            this.selection = this.selection.focusApplication(app.croppingId, app.plan.planId, app.appId)

            nutrient = nutrient || this.nutrient

            this._nutrients.setNutrient(nutrient, false)

            this.mapCtxt.selectViewFromSelection(this.nutrient, this.selection)
            this.mapCtxt.selectCropping(app.croppingId)

        }

    }

    public selectApplication2() {

        return ({ planId, appId }: { planId: number, appId: number }) => {
            const changeExtent = planId !== this.selection?.planId
            this.selectPlan(planId, appId, changeExtent)
        }

    }

    public selectPlan(planId: number, appId = 0, changeMapExtent = true) {

        const plan = this.allPlans.find(p => p.planId == planId)
        if (!plan) return

        this.plan = plan
        this.updateMruList(plan)

        this.cropping = null
        this.view = MenuView.PlanDetails

        if (isArrayEmpty(plan.applications)) {
            this.selection = this.selection.openPlan(plan.plan.NutritionPlanId)
        }
        else {
            appId = appId || plan.applications[0].NutritionPlanProductAppId
            this.selection = this.selection.focusApplication(0, planId, appId)
            this.selectAppNutrient(plan, appId)
        }

        this.mapCtxt.selectViewFromSelection(this.nutrient, this.selection)
        this.mapCtxt.clearSelection()

        if (changeMapExtent) {
            const targets = plan.cropping.map(c => c.cropping)
            this.mapCtxt.viewItemExtent(targets)
        }

    }

    public selectPlanCropping(planCropping: IPlanCropping) {

        this.selection = this.selection.focusPlanCropping(planCropping.croppingId)

        this.mapCtxt.selectCropping(planCropping.croppingId)

        this.setCurrentApp(null)

    }

    public selectCroppingPlan(croppingPlan: ICroppingPlan) {

        this.selection = this.selection.focusCroppingPlan(croppingPlan.planId)

        this.mapCtxt.selectCropping(croppingPlan.croppingId)

        this.setCurrentApp(null)

    }

    public isCroppingSelected(cropping: CroppingVm, closing: number) {
        let s = this.selection
        return s && (this.cropping == cropping) && (!s.planId) && (!s.appId) && ((s.closing || 0) == (closing || 0))
    }

    public isOpeningBalanceSelected(cropping: CroppingVm) {
        return this.isCroppingSelected(cropping, -1)
    }

    public isAppSelected(app: ICroppingApplication) {
        return (this.app && app && (this.app.appId == app.appId) && (this.app.croppingId == app.croppingId))
    }

    public isPlanCroppingSelected(c: IPlanCropping) {
        let s = this.selection
        return (!this.app) && s && (c.plan.NutritionPlanId == s.planId) && (c.croppingId == s.croppingId)
    }

    public isCroppingPlanSelected(p: ICroppingPlan) {
        let s = this.selection
        return (!this.app) && s && (p.planId == s.planId) && (p.croppingId == s.croppingId)
    }

    hasMultipleZones(zones: INtrReq[], nutrient: INutrient): boolean {
        let mask = Math.pow(2, nutrient.nutrient - 1)
        return zones.filter(z => z.NutrientGroup & mask).length > 1
    }

    protected applySelection() {

        let selection = this.selection
        if (!selection) return

        let app: AppVm

        if (selection.planMode) {

            let plan = this.allPlans.find(p => p.plan.NutritionPlanId == selection.planId)

            if (plan) {
                this.plan = plan
                this.view = MenuView.PlanDetails
                app = selection.appId ? plan.findApplication(selection.appId, selection.croppingId) : null
                this.updateMruList(plan)
            }

            this.view = plan ? MenuView.PlanDetails : MenuView.PlansIndex

        }
        else {

            let croppingId = selection.croppingId

            let cropping = this.allCropping.find(c => c.croppingId == croppingId)

            if (cropping) {
                this.cropping = cropping
                app = selection.appId ? this.cropping.findApplication(selection.appId) : null
                this.updateMruList(cropping)
            }

            this.view = cropping ? MenuView.CroppingDetails : MenuView.CroppingIndex

        }

        this.setCurrentApp(app)

    }

    protected applyFilters() {

        this.visibleCropping = (this.allCropping || []).filter(c => c.visible)
        this.visiblePlans = (this.allPlans || []).filter(p => p.visible)

        let cropFilter = this._filters ? this._filters.crop : null
        let textFilter = this._filters ? this._filters.getTextRegExp() : null

        if (cropFilter) {

            this.visibleCropping = this.visibleCropping.filter(f => f.croppingVm.cropId == cropFilter.CropId)

            this.visiblePlans = this.visiblePlans.filter(p => p.cropping && p.cropping.some(c => {
                let cropping = this.fieldsSvc.findCropping(c.croppingId)
                return (cropping && (cropping.cropId == cropFilter.CropId))
            }))

        }

        let s = this.selection

        if (textFilter) {

            if (s && s.croppingMode) {
                this.visibleCropping = this.visibleCropping.filter(f => textFilter.test(f.description))
            }
            else if (s && s.planMode) {
                this.visiblePlans = this.visiblePlans.filter(p => textFilter.test(p.name))
            }

        }

        const planFilter = this.planFilter

        if (planFilter) {
            this.visiblePlans = applyPlanFilter(this.visiblePlans, p => p.planInfo, planFilter)
        }

        if (s && s.croppingMode && this.cropping && (!this.visibleCropping.some(c => c == this.cropping))) {

            this.cropping = null
            s.clear(false)
            this.view = MenuView.CroppingIndex

            this.mapCtxt.clearSelection()

        }

        this.updateGroups()

    }

    private updateGroups() {

        if (!this.groupingSvc) return

        let grouping = this.groupingSvc.currentGrouping || this.groupingSvc.groupings[0]

        this.visibleCroppingGroups = grouping.grouping(this.visibleCropping)

    }

    private selectAppNutrient(plan: PlanVm, appId: number) {

        const app = plan.applications.find(a => a.NutritionPlanProductAppId == appId)

        if (app) {
            this._nutrients.setNutrient(app.TargetNutrient, false)
        }

    }

    public selectNutrient(value: NutrientType | string | INutrient, resetPlan?: boolean) {

        this._nutrients.setNutrient(value)

        if (resetPlan) {
            this.setCurrentApp(null)
        }

    }

    public getIndexData(def: IIndexDataAttr, target: any, nutrient: string) {
        return getIndexData(def, target, nutrient)
    }

    private setCurrentApp(vm: AppVm) {
        this.appVm = vm
        this.app = vm ? vm.app : null
    }

    private updateMruList(item: CroppingVm | PlanVm) {
        if (!item) return
        this.mruList = [item].concat(this.mruList.filter(i => i != item)).filter((i, index) => index < 20)
    }

    private getVisibleCropping(): CroppingVm[] {
        let visible = this.filters.cropping.map(c => c.croppingId)
        return this.allCropping.filter(c => visible.indexOf(c.croppingId) >= 0)
    }

    private getTargetCropping(group?: CroppingGroup, filterSelected: boolean = true): CroppingVm[] {

        if (this.cropping && this.croppingDetailsView) {
            return [this.cropping]
        }

        let cropping = group ? group.cropping : this.getVisibleCropping()

        let selected = (filterSelected ? cropping.filter(c => c.selected) : cropping)

        selected = selected.length > 0 ? selected : cropping

        return selected

    }

    private getTargetCroppingIds(group?: CroppingGroup): number[] {
        return this.getTargetCropping(group).map(c => c.croppingId)
    }

    public hasUnselectedCropping(target?: CroppingVm[] | CroppingGroup): boolean {
        let cropping = isArray(target) ? <CroppingVm[]>target : this.getTargetCropping(<CroppingGroup>target, false)
        return cropping && cropping.some(c => c.authorized && !c.selected)
    }

    public toggleCroppingSelection(group?: CroppingGroup) {
        let cropping = this.getTargetCropping(group, false)
        let select = this.hasUnselectedCropping(cropping)
        cropping.forEach(c => c.selected = select && c.authorized)
    }

    public canResetRequirements(group?: CroppingGroup): boolean {
        if (this.readOnly) return false
        if (this.croppingDetailsView) return true
        let cropping = this.getTargetCropping(group, true)
        return cropping.some(c => c.selected && c.authorized)
    }

    public resetRequirements(group?: CroppingGroup): ng.IPromise<boolean> {
        return this.context.resetRequirements(this.getTargetCroppingIds(group))
    }

    calculateRequirements(group?: CroppingGroup): ng.IPromise<boolean> {
        return this.context.calculateRequirements(this.getTargetCroppingIds(group))
    }

    canCalculateRequirements(group?: CroppingGroup) {
        return hasAuthorizationsOnAnyInGroup(this.getTargetCropping(group))
    }

    createPlan(group?: CroppingGroup): ng.IPromise<any> {
        return this.context.createPlan(this.getTargetCroppingIds(group))
    }

    canCreateNewPlan(group?: CroppingGroup) {
        return hasAuthorizationsOnAnyInGroup(this.getTargetCropping(group))
    }

    canCalculateAllPlans() {
        const { planIndexView, session: { permissions: { nutritionPlans: { calculateAllPlans } } } } = this
        return planIndexView && calculateAllPlans
    }

    calculateAllPlans() {

        const msg = `
            <p>This will recalculate all plans and applications.</p>
            <p>
               Using this command is the same as going through each plan in application order, and recalculating each with the "Recalculate all applications" option on.
            </p>
            <p>
                <i class="fas fa-exclamation-triangle text-danger"></i>&nbsp
                Please note that this will potentially change current application rates and stock requirements.
            </p>
            <p>Click 'Ok' to continue.</p>`

        const title = "Recalculate all plans"

        return shell.showOkCancel(this.shell, msg, title).then(ok => {

            if (ok) {

                const ids = getDistinctValues(
                    this.svc
                        .getRequirements()
                        .filter(r => !r.Zone),
                    r => r.CroppingId
                )

                return this.svc.calculateCroppingPlans(ids, true)

            }

        })

    }

    editPlan(plan: PlanVm): ng.IPromise<any> {
        return this.context.updatePlan(plan.plan)
    }

    getCropCssColor(crop: ICrop) {
        return getCropCssColor(crop)
    }

    editRequirements(target?: CroppingGroup | CroppingVm) {

        let cropping: CroppingVm[]

        if (target instanceof CroppingGroupVm) {
            cropping = target.getSelectedCropping()
            if (!cropping.length) cropping = target.cropping
        }
        else if (target instanceof CroppingVm) {
            cropping = [target].concat(this.allCropping.filter(c => c != target))
        }
        else {
            cropping = this.allCropping
        }

        let args = cropping.map(c => c.croppingVm)

        this.context.editRequirements(args)

    }

    updateOnIndexAttrChanged() {
        const attr = this.planIndexView ? this.planIndexAttr : this.croppingIndexAttr
        const view = attr ? attr.mapView : null
        const closing = this.croppingIndexView && (attr.mapView == "ob") ? - 1 : undefined
        this.selection = this.selection.setMapView(view, closing)
    }

    editApplicationZones(app: AppVm) {
        this.selectApplication(app)
        this.context.editApplicationZones()
    }

    viewExtent() {

        if (this.cropping) {
            this.mapCtxt.viewItemExtent(this.cropping.croppingVm)
        }

        if (this.plan) {
            const fvm = this.plan.cropping.map(c => c.cropping)
            this.mapCtxt.viewItemExtent(fvm)
        }

    }

}

